import * as React from 'react';
import {useState} from 'react';
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from 'react-intl';
import * as Yup from "yup";
import {Form, Formik, FormikProps} from "formik";
import {RouteComponentProps, withRouter} from "react-router";
import viaForSchools from "../../assets/viaForSchools.svg";
import {Button, Checkbox, FormControlLabel, Link} from "@material-ui/core";
import {PageContainer, FormContainer, RowContainer} from '../common/styles';
import {CognitoUser} from "amazon-cognito-identity-js";
import * as H from "history";
import {ToastType} from "../../stores/ui-state.store";
import ToastSnackBar from "../common/toastSnackBar";
import viaLogo from "../../assets/viaLogo.svg";
import {changePasswordValidation} from "../../utils/validation-utils";
import {SMS_MFA, SOFTWARE_TOKEN_MFA} from "../../constants/constants";
import {logEvent, MParticleEventName} from "../../utils/m-particle-utils";
import handleRedirect from "../../utils/redirectHelper";
import PasswordField from "../common/passwordField";

enum LoginFormFields {
    Password = 'Password',
    ConfirmPassword = 'ConfirmPassword',
    AgreeToTerms = 'AgreeToTerms',
}

interface OwnProps {
    user: CognitoUser | any;
    authClient: any;
    isViaForSchools: boolean;
    queryParams: string;
}

interface FormValues {
    [LoginFormFields.AgreeToTerms]: string;
    [LoginFormFields.Password]: string;
    [LoginFormFields.ConfirmPassword]: string;
}

const getValidationSchema = (intl: InjectedIntl, oldPassword: string) => Yup.object().shape({
    [LoginFormFields.Password]: changePasswordValidation(intl.messages['login.change-password.required'],
        intl.messages['login.change-password.not-valid-pass'],
        intl.messages['login.change-password.cannot-change-to-previous-password'], oldPassword),
    [LoginFormFields.ConfirmPassword]: Yup.string().required(intl.messages['login.change-password.required'])
        .oneOf([Yup.ref(LoginFormFields.Password), null], (intl.messages['login.change-password.password-not-match'])),
});

const initialValues: FormValues = {
    [LoginFormFields.Password]: '',
    [LoginFormFields.AgreeToTerms]: "false",
    [LoginFormFields.ConfirmPassword]: '',
};

const changePassword = async (password: string, user: CognitoUser | any, history: H.History,
                              authClient: any, queryParams: string,
                              setShowToast: (showToast: boolean) => void,
                              setToastMessage: (toastMessage: string) => void) => {
    authClient.completeNewPassword(user, password, {}).then(() => {
        const username = user.getUsername().toLowerCase();
        logEvent(MParticleEventName.CHANGE_PASSWORD_SUCCESS, ChangePassword.displayName,{username: username,});
        if (user.challengeName === 'MFA_SETUP') {
            authClient.setupTOTP(user).then((code: any) => {
                history.push({pathname: '/mfaSetup', search: queryParams,
                    state: {code: code, username: user.getUsername(), passwordWasSet: true}});
            });
        } else if (user.challengeName === SOFTWARE_TOKEN_MFA) {
            history.push({pathname: '/mfaValidation', search: queryParams, state: {challenge: user.challengeName,
                    passwordWasSet: true}});
        } else if (user.challengeName === SMS_MFA) {
            let phoneNumber = user.challengeParam ?  user.challengeParam['CODE_DELIVERY_DESTINATION'] : null;
            phoneNumber = phoneNumber.replace('+', '*');
            history.push({pathname: '/mfaValidation', search: queryParams, state: {challenge: user.challengeName,
                    phoneNumber: phoneNumber, passwordWasSet: true}});
        } else {
            logEvent(MParticleEventName.LOGIN_SUCCESS, ChangePassword.displayName, {username: username,});
            handleRedirect(history, queryParams);
        }
    }).catch (error => {
        // Known bug in aws-amplify: https://github.com/aws-amplify/amplify-js/issues/4321
        if (error.message && error.message.indexOf('totpRequired is not a function') !== -1){
            history.push({pathname: '/mfaValidation', state: {challenge: SOFTWARE_TOKEN_MFA, passwordWasSet: true}});
        }
        else{
            logEvent(MParticleEventName.CHANGE_PASSWORD_FAILURE, ChangePassword.displayName,
                {username: user.getUsername().toLowerCase(), errorCode: error.code, errorMessage: error.message,});
        }
        setToastMessage('login.change-password.general-error-message');
        setShowToast(true);
        if (error.code === 'NotAuthorizedException') {
            if ((error.message === 'Invalid session provided for the user.') ||
                (error.message === 'Invalid session for the user, session is expired.')) {
                setToastMessage('login.change-password.session-timed-out');
            }
        }
    });
};

type Props = InjectedIntlProps & FormikProps<FormValues> & RouteComponentProps & OwnProps;

const ChangePassword = (props: Props) => {
    const { user, history, location, intl, authClient, isViaForSchools, queryParams} = props;
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('login.change-password.general-error-message');

    if (!user.user) {
        history.push('/login');
    }
    // @ts-ignore
    const oldPassword = location.state && location.state.oldPassword;
    const validationSchema = getValidationSchema(intl, oldPassword);

    const [passwordShown, setPasswordShown] = useState(false);
    const togglePasswordVisibility = () => {
        setPasswordShown(passwordShown ? false : true);
    };

    const [confirmPasswordShown, setConfirmPasswordShown] = useState(false);
    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordShown(confirmPasswordShown ? false : true);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={ async (fields, {setSubmitting}) => {
                const password = fields[LoginFormFields.Password];
                const isAgreedToTerms = fields[LoginFormFields.AgreeToTerms] === "true";
                if (isAgreedToTerms) {
                    await changePassword(password, user.user, history,
                        authClient, queryParams, setShowToast, setToastMessage);

                } else {
                    setToastMessage('login.change-password.agree-to-terms-required');
                    setShowToast(true);
                }
                setSubmitting(false);
            }}
            render={({ values, setFieldValue }) => (
                <PageContainer>
                    <FormContainer>
                        <RowContainer style={{backgroundColor: "#F3F5F8", margin: 0}}>
                            <div style={{padding: "20px", margin: "auto"}}>
                                { isViaForSchools ? <img alt="" src={viaForSchools}/> : <img alt="" src={viaLogo}/>}
                            </div>
                        </RowContainer>
                        <Form style={{ display: 'flex', flexDirection: 'column' }}>
                            <RowContainer style={{padding: "15px 60px 0px", flexDirection: "column"}}>
                                <span style={{margin: "auto", padding: "8px", color: "#555555"}}>
                                    <FormattedMessage id='login.via.change-pass-message'/>
                                </span>
                                <PasswordField name={LoginFormFields.Password}
                                               label={intl.messages['login.change-password.password-label']}
                                               id={'change-password.password-field'}
                                               passwordShown={passwordShown}
                                               togglePasswordVisibility={togglePasswordVisibility}
                                               style={{marginTop: "16px"}}/>
                                <PasswordField name={LoginFormFields.ConfirmPassword}
                                               label={intl.messages['login.change-password.confirm-password-label']}
                                               id={'change-password.confirm-password-field'}
                                               passwordShown={confirmPasswordShown}
                                               togglePasswordVisibility={toggleConfirmPasswordVisibility}
                                               style={{margin: "16px 0px 5px"}}/>
                                <FormControlLabel
                                    label={<Label isViaForSchools={isViaForSchools}/>}
                                    name={LoginFormFields.AgreeToTerms}
                                    control={<Checkbox style={{color: "#F5771D"}} />}
                                    checked={values[LoginFormFields.AgreeToTerms] === "true"}
                                    onChange={() => setFieldValue(LoginFormFields.AgreeToTerms,
                                                      values[LoginFormFields.AgreeToTerms]
                                                      === "true" ? "false" : "true")}
                                />
                                <Button type="submit"
                                        style={{width: "100%", marginTop: "10px", marginBottom: "60px", height: "40px",
                                            backgroundColor: "#20B7E9", color: "white"}}>
                                    <FormattedMessage id='login.change-password.change-password-button' />
                                </Button>
                            </RowContainer>
                        </Form>
                    </FormContainer>
                    <ToastSnackBar show={showToast}
                                   message={intl.messages[toastMessage]}
                                   toastType={ToastType.ERROR}
                                   onClose={() => {setShowToast(false); }}/>
                </PageContainer>
            )} />
    );
};


interface LabelProps {
    isViaForSchools: boolean;
}

const Label = (props: LabelProps) => {
    return (
        <span style={{fontSize: "0.8rem", color: "#555555"}}>
            <FormattedMessage id='login.change-password.agree' />
            {props.isViaForSchools &&
                <React.Fragment>
                    <Link target="_blank" href={"https://ridewithvia.com/vfsconsole"} rel="noopener"
                          style={{margin: "0px 5px", color: "#007CAD"}}>
                        <FormattedMessage id='login.change-password.terms-of-use'/>
                    </Link>
                    < FormattedMessage id='login.change-password.and' />
                </React.Fragment>
            }
            <Link target="_blank" href={"https://ridewithvia.com/privacy-policy/"} rel="noopener" style={{margin: "0px 5px", color: "#007CAD"}}>
                <FormattedMessage id='login.change-password.privacy-policy' />
            </Link>
        </span>
    );
};

ChangePassword.displayName = 'ChangePassword';
export default withRouter(injectIntl(ChangePassword));
